import { Box, colors } from '@mui/material';
import { JSXElementConstructor, MouseEvent, PropsWithChildren, ReactElement, useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TrendData } from '../../../../../common/dashboards/models/TrendData';
import { TrendDataScale } from '../../../../../common/dashboards/models/TrendDataScale';
import { DateRange } from '../../../../../common/dashboards/types';
import { SEVERITY_COLORS, Severity } from '../../../../../common/enums/Severity';
import { extractTrendsDataScale, formatRange } from '../../../utils';
import { ChartTooltip } from './ChartTooltip';
import { BarPayload } from './types';

type Props = {
  title?: string;
  trends: TrendData[];
  includePassed: boolean;
  onUpdateDateRange: (dateRange: DateRange) => void;
  onSelectTrend: (value: TrendData | null) => void;
  printConfig?: {
    width: number;
    height: number;
  } | null;
};

const formatAxisNumber = (value: number) => {
  if (value > 1_000_000) {
    return `${(value / 1_000_000).toFixed(2)}m`;
  }

  if (value > 1000) {
    return `${(value / 1000).toFixed(2)}k`;
  }

  return value.toString();
};

export const TrendsChart = ({ title, trends, includePassed, onUpdateDateRange, onSelectTrend, printConfig = null }: Props) => {
  const scale = useMemo(() => {
    const dates = trends.flatMap((item) => [item.getFrom(), item.getTo()]);

    return extractTrendsDataScale(dates);
  }, [trends]);

  // we do this for animation
  const displayData = trends.map((item) => ({
    name: item.getFormattedDate(scale),
    // tooltip value should have first next scale, clipped of at seconds
    rangeName: formatRange([item.getFrom(), item.getTo()], Math.min(scale + 1, TrendDataScale.SECONDS) as TrendDataScale),
    high: item.getHighInstances(),
    med: item.getMediumInstances(),
    low: item.getLowInstances(),
    passed: includePassed ? item.getPassedInstances() : 0,
    dateRange: [item.getFrom(), item.getTo()],
    item,
  }));

  const handleBarClick = (barPayload: BarPayload, event: MouseEvent<SVGPathElement>) => {
    event.stopPropagation();
    event.preventDefault();

    onSelectTrend(barPayload.payload.item as TrendData);
  };
  const resetSelectedTrends = () => {
    onSelectTrend(null);
  };

  const handleBarDoubleClick = (barPayload: BarPayload) => {
    onUpdateDateRange(barPayload.payload.dateRange);
  };

  // Wrap container so that we can have proper print.
  // Responsive container doesn't behave well on Safari and Firefox when printing.
  // Not the prettiest solution, but we need to make width static.
  const Container = useMemo(() => {
    if (printConfig) {
      return ({ children }: PropsWithChildren<{}>) => (
        <Box width={printConfig.width} height={printConfig.height}>
          {children}
        </Box>
      );
    }

    return ({ children }: PropsWithChildren<{}>) => (
      <ResponsiveContainer width="99%" height={200}>
        {children as ReactElement<unknown, string | JSXElementConstructor<unknown>>}
      </ResponsiveContainer>
    );
  }, [printConfig]);

  return (
    <Container>
      <BarChart
        aria-label={title}
        data={displayData}
        onClick={() => resetSelectedTrends()}
        accessibilityLayer={true}
        role="graphics-document"
        {...(printConfig ? { width: printConfig.width, height: printConfig.height } : {})}
      >
        <CartesianGrid vertical={false} />
        <XAxis dataKey="name" axisLine={false} tickLine={false} padding={{ left: 20 }} minTickGap={16} />
        <YAxis axisLine={false} tickLine={false} tickFormatter={(value) => formatAxisNumber(value as number)} />
        <Tooltip cursor={{ fill: colors.grey[200] }} content={<ChartTooltip includePassed={includePassed} />} />
        {(['high', 'med', 'low', 'passed'] as Severity[]).map((key) => (
          <Bar
            key={key}
            dataKey={key}
            stackId="bar-data"
            fill={SEVERITY_COLORS[key].main}
            activeBar={{ cursor: 'pointer' }}
            onClick={(barPayload: BarPayload, index, event) => handleBarClick(barPayload, event)}
            onDoubleClick={(barPayload: BarPayload) => handleBarDoubleClick(barPayload)}
            isAnimationActive={!printConfig}
          />
        ))}
      </BarChart>
    </Container>
  );
};
